import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {
  emptyOverride,
  NameTranslationType, OverrideInstruction, OverridePositionRequest,
  OverridePositionsMap, PositionTypeEnum,
  TagDto, TagOverride,
  TagRequestDto
} from "app/models/menu.model";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {Store} from "@ngrx/store";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MenuService} from "app/services/menu.service";
import {Organization} from "app/models/organization.model";
import {TagService} from "app/services/tag.service";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {BaseModalComponent} from "app/shared/base-modal/base-modal.component";
import {MenuOverrideService} from "app/services/menu-override.service";

@UntilDestroy()
@Component({
  selector: 'app-edit-tag-modal',
  templateUrl: './edit-tag-modal.component.html',
  styleUrls: ['./edit-tag-modal.component.scss']
})
export class EditTagModalComponent extends BaseModalComponent implements OnInit {
  tagNames: Set<string> = new Set();
  initValues = {}
  form: FormGroup;
  overridePositionsMap: OverridePositionsMap;
  selectedTagImgSrc: string;
  constructor(
    private store: Store,
    private dialog: MatDialog,
    private translate: TranslateService,
    private menuService: MenuService,
    private tagSerivce: TagService,
    public dialogRef: MatDialogRef<EditTagModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditTagModalComponentDataType
  ) {
    super(dialogRef);
    for (const tagName of data.tagNames) {
      if(data.tag.name !== tagName) {
        this.tagNames.add(tagName);
      }
    }
    this.initValues["tagName"] = data.tag.name;
    this.initValues["picture"] = this.data.tag.externalPictureUrl ?? '';
    this.initValues["textColor"] = this.data.tag.textColor ?? '';
    this.initValues["backgroundColor"] = this.data.tag.backgroundColor ?? '';
    this.form = new FormGroup({});
    this.form.addControl('textColor', new FormControl(data.tag.textColor ?? ''));
    this.form.addControl('backgroundColor', new FormControl(data.tag.backgroundColor ?? ''));
    this.form.addControl('externalPictureUrl', new FormControl(data.tag.externalPictureUrl ?? ''));
    this.form.addControl('tagName', new FormControl(data.tag.name ?? ''));
    this.form.addControl('isManuallyCreated', new FormControl({value: this.isManuallyCreated(), disabled: true}));

    if(!data.tag.nameTranslations){
      data.tag.nameTranslations = {} as NameTranslationType;
    }

    for(const lang of this.data.organization.additionalLanguages){
      this.initValues[lang] = data.tag.nameTranslations[lang] ?? null;
      this.form.addControl(lang, new FormControl(data.tag.nameTranslations[lang] ?? ''))
    }
    this.selectedTagImgSrc = this.data.tag.externalPictureUrl ?? '';
  }

  ngOnInit(): void {
    if (!this.isManuallyCreated()) {
      this.store.select(MenuSelectors.selectOverridePositionsMap)
        .pipe(untilDestroyed(this))
        .subscribe(overridePositionsMap => {
          this.overridePositionsMap = overridePositionsMap;
        });
    }
  }

  async submit() {
    if(!this.validate()){
      return;
    }
    const translations: NameTranslationType = {};
    for (const lang of this.data.organization.additionalLanguages) {
      translations[ lang ] = this.form.value[ lang ];
    }
    const requestDto = {
      name: this.form.value[ 'tagName' ],
      nameTranslations: translations,
      externalPictureUrl: this.form.get('externalPictureUrl').value,
      textColor: this.form.get('textColor').value,
      backgroundColor: this.form.get('backgroundColor').value,
    } as TagRequestDto;

    if (this.data.tagId == null) {
      const response = await this.tagSerivce.create(this.data.menuId, requestDto);
      const result = {created: response};
      this.exit(result);
    } else if (this.isManuallyCreated()) {
        const response = await this.tagSerivce.update(this.data.tagId, requestDto);
        const result = {updated: response};
        this.exit(result);
    } else {
      const result = {updated: {...this.data.tag, ...requestDto}};
      await this.saveTagOverrides();
      this.exit(result);
    }
  }

  private async saveTagOverrides(tagId?: any) {
    let allergenGroupOverride: TagOverride;
    let isOverrideExists: boolean;
    let overridePosition = this.overridePositionsMap?.getOverride(PositionTypeEnum.Tags, this.data.tagId ?? tagId);
    // if override position does not exists create a new one
    if (!overridePosition) {
      overridePosition = {
        positionType: PositionTypeEnum.Tags,
        positionId: this.data.tagId ?? tagId,
        override: emptyOverride,
        menuSectionId: this.data.menuSectionId,
      };
      allergenGroupOverride = {};
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
      allergenGroupOverride = JSON.parse(overridePosition.override) as TagOverride;
    }

    const overrideInstructions: OverrideInstruction[] = []

    if(this.data.originalTag.externalId){
      if (this.initValues[ 'tagName' ].trim() !== this.form.value[ 'tagName' ].trim()) {
        MenuOverrideService.overrideTextFiled(this.data.tag, this.data.originalTag, 'name', this.form.value[ "tagName" ], isOverrideExists, overrideInstructions);
      }
      if (this.initValues[ 'textColor' ].trim() !== this.form.value[ 'textColor' ].trim()) {
        MenuOverrideService.overrideTextFiled(this.data.tag, this.data.originalTag, 'textColor', this.form.value[ "textColor" ], isOverrideExists, overrideInstructions);
      }
      if (this.initValues[ 'backgroundColor' ].trim() !== this.form.value[ 'backgroundColor' ].trim()) {
        MenuOverrideService.overrideTextFiled(this.data.tag, this.data.originalTag, 'backgroundColor', this.form.value[ "backgroundColor" ], isOverrideExists, overrideInstructions);
      }

      if (this.data.organization.additionalLanguages) {
        for (const lang of this.data.organization.additionalLanguages) {
          if (this.initValues[ lang ] !== this.form.value[ lang ]) {
            MenuOverrideService.overrideTranslationFiled(this.data.tag, this.data.originalTag, 'nameTranslations', lang, this.form.value[ lang ], isOverrideExists, allergenGroupOverride, overrideInstructions)
          }
        }
      }
    }

    if (this.initValues['externalPictureUrl'] !== this.form.value['externalPictureUrl']) {
      MenuOverrideService.overrideTextFiled(this.data.tag, this.data.originalTag, 'externalPictureUrl', this.selectedTagImgSrc, isOverrideExists, overrideInstructions, true);
    }

    const menuSectionId = this.data.menuSectionId;
    if (overrideInstructions.length > 0) {

      const overridePositionRequest: OverridePositionRequest = {
        ...overridePosition,
        instructions: overrideInstructions,
      };

      await this.menuService.saveOverridePositions(menuSectionId, [overridePositionRequest], [this.data.menuSectionId])
        .then(_=> _)
        .catch(error => alert(error.error));
    }
  }

  async remove() {
    if(confirm(this.translate.instant('MENU.DISHES.NEW.CONFIRM_DELETE_TAG'))){
      await this.tagSerivce.delete(this.data.tagId);
      this.exit({deleted: this.data.tagId})
    }
  }

  isManuallyCreated() : boolean {
    return this.data.tag.isManuallyCreated;
  }

  restoreName() {
    this.form.get('tagName').setValue(this.data.originalTag.name);
  }

  isNameDifferent() {
    return this.data.originalTag && this.form.value.allergenGroupName !== this.data.originalTag.name;
  }

  onImageChanged($event: string) {
    this.form.get('externalPictureUrl').setValue($event);
    this.selectedTagImgSrc = $event;
  }

  validate() {
    const tagName = this.form.value[ 'tagName' ];
    if(!tagName){
      this.form.setErrors({'tagName': "Name is required"});
      return false;
    }
    if(this.tagNames.has(tagName)){
      this.form.setErrors({'tagName': "Name should be unique"});
      return false;
    }

    return true;
  }
}

export interface EditTagModalComponentDataType {
  tagId: string | null,
  tag: TagDto,
  originalTag: TagDto,
  organization: Organization,
  menuSectionId: string,
  menuId: string,
  tagNames?: Set<string>,
}
